import { Button } from '@library/components/buttons';
import { MethodsCell } from '@library/components/table/payments/methods/MethodsCell';
import type { PaymentsCard } from '@library/types/payments';
import { Tooltip } from '@library/components/tooltip/Tooltip';
import TrashIcon from '@library/assets/images/icons/trash-icon.svg';
import { classNames } from '@library/utils';
import { useNormalizedMethodData } from '@library/components/table/payments/methods/hooks/useNormalizeMethodData';

export interface DepositsRowProps {
  method: PaymentsCard;
  onRowClick: () => void;
  onDestroy: () => void;
  isDeleting: boolean;
}

const MethodsAction = ({
  cardData,
  disabled,
  onClick
}: {
  cardData: PaymentsCard;
  disabled: boolean;
  onClick: () => void;
}) => {
  const { data } = useNormalizedMethodData(cardData);

  if (data.deleted) {
    return (
      <MethodsCell>
        <span className="text-soClean-180 flex opacity-50">Deleted</span>

        <span className="text-msJackson cursor-pointer">
          <Tooltip text={`deleted by ${data.deletedBy} at ${data.deletedAt}`}>
            info
          </Tooltip>
        </span>
      </MethodsCell>
    );
  }

  return (
    <MethodsCell>
      <Button onClick={onClick} disabled={disabled}>
        <img
          alt="remove card"
          className="size-4 cursor-pointer"
          title="remove card"
          src={TrashIcon}
        />
      </Button>
    </MethodsCell>
  );
};

export const MethodsRow = ({
  method,
  onRowClick,
  onDestroy,
  isDeleting
}: DepositsRowProps) => {
  const { data: normalizedMethodData } = useNormalizedMethodData(method);
  const methodCellOpacity = normalizedMethodData.deleted ? 'opacity-50' : '';

  const handleRowClick = (
    event: React.MouseEvent<
      HTMLButtonElement | HTMLTableRowElement | HTMLImageElement
    >
  ) => {
    // This is an action click (icons are svg images),
    // so we don't want to trigger the row modal in that case
    if ('tagName' in event.target && event.target.tagName === 'IMG') {
      return;
    }

    onRowClick();
  };

  return (
    <tr
      className={classNames(
        'border-soFresh-140 h-14 items-center border-b',
        'cursor-pointer',
        'hover:bg-soFresh-120'
      )}
      onClick={handleRowClick}
    >
      <MethodsCell className={methodCellOpacity}>
        {normalizedMethodData.type}
      </MethodsCell>
      <MethodsCell className={methodCellOpacity}>
        {normalizedMethodData.lastDigits}
      </MethodsCell>
      <MethodsCell className={methodCellOpacity}>
        {normalizedMethodData.exp}
      </MethodsCell>
      <MethodsCell className={methodCellOpacity}>
        {normalizedMethodData.zip}
      </MethodsCell>
      <MethodsCell className={methodCellOpacity}>
        {normalizedMethodData.name}
      </MethodsCell>
      <MethodsAction
        onClick={onDestroy}
        cardData={method}
        disabled={isDeleting}
      />
    </tr>
  );
};
